import type { RouteLocationNormalized } from 'vue-router'

/**
 * Finds the first route or parent route matching the given predicate.
 * Starting from the current route, and going through every parent route until we hit the root route.
 *
 * @param route - The current route.
 * @param predicate - The predicate to use to find the first matching route.
 * @returns The first matching route in the parent hierarchy, from child to parent.
 */
export function findRouteOrParentMatchingPredicate(
  route: RouteLocationNormalized,
  predicate: (
    routeDefinition: RouteLocationNormalized['matched'][number],
  ) => boolean,
): RouteLocationNormalized['matched'][number] | undefined {
  // Matches are listed starting from the first parent, and going through every route
  // until we hit the current route. For example, having three routes (A > B > C)
  // would generate a `matched` array [A, B, C]. Since the route defined latter
  // should take precedence over its parents, we reverse the whole array.
  const reversed = [...route.matched].reverse()

  // Since we reversed the array beforehand, we can now use `Array#find` to traverse
  // the whole array and return the _first_ route matching the given predicate.
  return reversed.find(predicate)
}
